import { useEffect, useRef } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { requestProducts } from 'src/redux/products/thunks/requestProducts';
import { TriggerName } from 'src/redux/products/types';
import { getFacetChangeMetadata } from 'src/redux/productsQuery/helpers/getFacetChangeMetadata';
import { getProductsQuerySyncPayloadFromURL } from 'src/redux/productsQuery/helpers/getProductsQuerySyncPayloadFromURL';
import { getRouteChangeTriggerName } from 'src/redux/productsQuery/helpers/getRouteChangeTriggerName';
import { productsQueryMatchesURL } from 'src/redux/productsQuery/helpers/productsQueryMatchesURL';
import { actions } from 'src/redux/productsQuery/slice';
import type { AppThunkDispatch } from 'src/redux/store';
import { scrollToTopOfProductsList } from 'src/utils/scrollToTopOfProductsList';
import { consumePendingProductsQueryURLSyncIntent } from './productsQueryURLSyncIntent';

export const useSyncProductsQueryFromUrl = () => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const router = useRouter();
  const lastHandledAsPath = useRef<string | null>(null);
  const productsQuery = useSelector((state: ReduxApplicationState) => state.productsQuery);
  const state = useSelector((s: ReduxApplicationState) => s);

  useEffect(() => {
    if (!router.isReady || lastHandledAsPath.current === router.asPath) {
      return;
    }

    const nextActive = getProductsQuerySyncPayloadFromURL(router.asPath, router.query, state);

    if (productsQueryMatchesURL(router.asPath, state)) {
      lastHandledAsPath.current = router.asPath;
      return;
    }
    lastHandledAsPath.current = router.asPath;

    batch(() => {
      const intent = consumePendingProductsQueryURLSyncIntent(router.asPath);
      const triggerName = intent?.triggerName ?? getRouteChangeTriggerName(state, nextActive);
      const metadata =
        intent?.metadata ??
        (triggerName === TriggerName.CHANGE_FACETS
          ? getFacetChangeMetadata(productsQuery.facets, nextActive.activeFacetValuesByKey)
          : undefined);

      dispatch(actions.syncFromURL(nextActive));
      dispatch(requestProducts({ metadata, pageLoading: nextActive.page, triggerName }));
    });

    scrollToTopOfProductsList();
  }, [dispatch, productsQuery, router.asPath, router.isReady, router.query, state]);
};
